/**
 * Dashboard tile arithmetic, kept pure so it can be tested without a browser.
 *
 * Layout is expressed as a size token rather than free pixels. Two reasons:
 * the tokens reflow correctly on a phone without a second layout to maintain,
 * and `{order, size, height}` converts mechanically to `{x, y, w, h}` if this
 * ever outgrows drag-to-reorder.
 */

export type TileSize = 'sm' | 'md' | 'lg' | 'full';
export type TileHeight = 'short' | 'tall';

export type DashboardTile = {
  id: string;
  chartId: string;
  size: TileSize;
  height: TileHeight;
  titleOverride?: string | null;
  dateRangeOverride?: unknown;
};

/** Column span out of twelve, at the large breakpoint. */
export const SIZE_COLUMNS: Record<TileSize, number> = {
  sm: 3,
  md: 4,
  lg: 6,
  full: 12,
};

export const SIZE_LABELS: Record<TileSize, string> = {
  sm: 'Small',
  md: 'Medium',
  lg: 'Large',
  full: 'Full width',
};

/**
 * The same four, short enough to sit side by side as a segmented control.
 * The full labels stay as each button's tooltip, so nothing is lost.
 */
export const SIZE_SHORT_LABELS: Record<TileSize, string> = {
  sm: 'S',
  md: 'M',
  lg: 'L',
  full: 'Full',
};

export const HEIGHT_PIXELS: Record<TileHeight, number> = {
  short: 240,
  tall: 420,
};

/**
 * Tailwind class for a tile. Full width below the large breakpoint, because a
 * quarter-width chart on a phone is unreadable.
 */
export const tileClassName = (size: TileSize) => {
  switch (size) {
    case 'sm':
      return 'col-span-12 md:col-span-6 lg:col-span-3';
    case 'md':
      return 'col-span-12 md:col-span-6 lg:col-span-4';
    case 'lg':
      return 'col-span-12 lg:col-span-6';
    default:
      return 'col-span-12';
  }
};

/** Moves a tile, returning a new array. Out-of-range indexes are a no-op. */
export const moveTile = (
  tiles: DashboardTile[],
  from: number,
  to: number
): DashboardTile[] => {
  const list = [...(tiles ?? [])];
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= list.length ||
    to >= list.length
  ) {
    return list;
  }
  const [moved] = list.splice(from, 1);
  list.splice(to, 0, moved);
  return list;
};

export const moveTileById = (
  tiles: DashboardTile[],
  activeId: string,
  overId: string
): DashboardTile[] => {
  const from = (tiles ?? []).findIndex((t) => t.id === activeId);
  const to = (tiles ?? []).findIndex((t) => t.id === overId);
  if (from < 0 || to < 0) return [...(tiles ?? [])];
  return moveTile(tiles, from, to);
};

export const setTileSize = (
  tiles: DashboardTile[],
  tileId: string,
  size: TileSize
): DashboardTile[] =>
  (tiles ?? []).map((tile) => (tile.id === tileId ? { ...tile, size } : tile));

export const setTileHeight = (
  tiles: DashboardTile[],
  tileId: string,
  height: TileHeight
): DashboardTile[] =>
  (tiles ?? []).map((tile) =>
    tile.id === tileId ? { ...tile, height } : tile
  );

export const removeTile = (tiles: DashboardTile[], tileId: string) =>
  (tiles ?? []).filter((tile) => tile.id !== tileId);

/**
 * Drops tiles whose chart no longer exists.
 *
 * Charts are soft-deleted and shared across the company, so a colleague can
 * remove one that is pinned to someone else's dashboard. That must degrade
 * quietly rather than throw while rendering.
 */
export const reconcileTiles = (
  tiles: DashboardTile[],
  availableChartIds: string[] | null | undefined
): DashboardTile[] => {
  if (!availableChartIds) return [...(tiles ?? [])];
  const available = new Set(availableChartIds);
  return (tiles ?? []).filter((tile) => tile && available.has(tile.chartId));
};

/** True when reconciliation would change the layout, so it is worth saving. */
export const needsReconcile = (
  tiles: DashboardTile[],
  availableChartIds: string[] | null | undefined
) => reconcileTiles(tiles, availableChartIds).length !== (tiles ?? []).length;
